import { rtkApi } from '@/shared/api/rtkApi';
import {
  GET_CERTIFICATES_COURSES,
  GET_CERTIFICATES_BY_COURSE,
  GET_CERTIFICATES_SIGNATURE,
} from '@/shared/api/rtkApi/tags';
import { LocalStorageService } from '@/shared/services/localStorage.service';
import {
  CertificatesCoursesResponse,
  CertificatesCoursesParams,
  CourseCertificatesResponse,
  CourseCertificatesParams,
  CertificatesFiltersResponse,
  EligibleEnrollmentsResponse,
  EligibleEnrollmentsParams,
  DeleteCertificatesBatchRequest,
  DeleteCertificatesBatchResponse,
  IssueCertificatesBatchRequest,
  IssueCertificatesBatchResponse,
  IssueCertificateRequest,
  IssueCertificateResponse,
  CertificateSignatureResponse,
  UpsertCertificateSignatureRequest,
} from './types';

export const certificatesApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    getCertificatesCourses: build.query<
      CertificatesCoursesResponse,
      CertificatesCoursesParams | void
    >({
      query: (params) => ({
        url: '/admin/certificates/courses',
        params: params && params.search ? { search: params.search } : undefined,
      }),
      providesTags: [GET_CERTIFICATES_COURSES],
    }),
    getCertificatesByCourse: build.query<CourseCertificatesResponse, CourseCertificatesParams>({
      query: ({ courseId, intakeId, groupNumber, search, page, pageSize, sort }) => ({
        url: `/admin/certificates/courses/${courseId}`,
        params: {
          ...(intakeId !== null && { intakeId }),
          ...(groupNumber !== null && { groupNumber }),
          ...(search && { search }),
          ...(page !== null && { page }),
          ...(pageSize !== null && { pageSize }),
          ...(sort && { sort }),
        },
      }),
      providesTags: [GET_CERTIFICATES_BY_COURSE],
    }),
    getCertificatesCourseFilters: build.query<CertificatesFiltersResponse, number>({
      query: (courseId) => ({
        url: `/admin/certificates/courses/${courseId}/filters`,
      }),
      providesTags: [GET_CERTIFICATES_BY_COURSE],
    }),
    getEligibleEnrollments: build.query<EligibleEnrollmentsResponse, EligibleEnrollmentsParams>({
      query: ({ courseId, intakeId, groupNumber }) => ({
        url: `/admin/certificates/courses/${courseId}/eligible-enrollments`,
        params: {
          ...(intakeId !== null && { intakeId }),
          ...(groupNumber !== null && { groupNumber }),
        },
      }),
      providesTags: [GET_CERTIFICATES_BY_COURSE],
    }),
    deleteCertificatesBatch: build.mutation<
      DeleteCertificatesBatchResponse,
      DeleteCertificatesBatchRequest
    >({
      query: (body) => ({
        url: '/admin/certificates/delete-batch',
        method: 'POST',
        body,
      }),
      invalidatesTags: [GET_CERTIFICATES_BY_COURSE, GET_CERTIFICATES_COURSES],
    }),
    downloadCertificatesBatch: build.mutation<Blob, DeleteCertificatesBatchRequest>({
      query: (body) => ({
        url: '/admin/certificates/download-batch',
        method: 'POST',
        body,
        responseHandler: (response: any) => response.blob(),
      }),
    }),
    issueCertificatesBatch: build.mutation<
      IssueCertificatesBatchResponse,
      IssueCertificatesBatchRequest
    >({
      query: (body) => ({
        url: '/admin/certificates/issue-batch',
        method: 'POST',
        body,
      }),
      invalidatesTags: [GET_CERTIFICATES_BY_COURSE, GET_CERTIFICATES_COURSES],
    }),
    issueCertificate: build.mutation<
      IssueCertificateResponse,
      IssueCertificateRequest
    >({
      query: (body) => ({
        url: '/admin/certificates/issue',
        method: 'POST',
        body,
      }),
      invalidatesTags: [GET_CERTIFICATES_BY_COURSE, GET_CERTIFICATES_COURSES],
    }),
    getCertificateSignature: build.query<CertificateSignatureResponse, void>({
      query: () => ({
        url: '/admin/certificates/signature',
      }),
      providesTags: [GET_CERTIFICATES_SIGNATURE],
    }),
    upsertCertificateSignature: build.mutation<
      CertificateSignatureResponse,
      UpsertCertificateSignatureRequest
    >({
      queryFn: async ({ signatureFile }) => {
        const formData = new FormData();
        formData.append('signatureFile', signatureFile);

        const accessToken = LocalStorageService.getAccessToken();
        const headers = new Headers();
        if (accessToken) {
          headers.append('Authorization', `Bearer ${accessToken}`);
        }
        headers.append('Accept', 'application/json');

        try {
          const apiUrl = typeof __API__ === 'string' ? __API__ : import.meta.env.VITE_API_URL || '';
          const response = await fetch(`${apiUrl}/admin/certificates/signature`, {
            method: 'PUT',
            headers,
            credentials: 'include',
            body: formData,
          });

          const data = await response.json();

          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data,
              },
            };
          }

          return { data };
        } catch (error: any) {
          return {
            error: {
              status: 'FETCH_ERROR',
              error: error.message,
            },
          };
        }
      },
      invalidatesTags: [GET_CERTIFICATES_SIGNATURE],
    }),
  }),
});

export const {
  useGetCertificatesCoursesQuery,
  useGetCertificatesByCourseQuery,
  useGetCertificatesCourseFiltersQuery,
  useGetEligibleEnrollmentsQuery,
  useDeleteCertificatesBatchMutation,
  useDownloadCertificatesBatchMutation,
  useIssueCertificatesBatchMutation,
  useIssueCertificateMutation,
  useGetCertificateSignatureQuery,
  useUpsertCertificateSignatureMutation,
} = certificatesApi;
